import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  DEFAULT_TERMINAL_APPROVAL_POLICY,
  TERMINAL_APPROVAL_RULES_MAX_COUNT,
  TerminalApprovalRuleSchema,
  type TerminalApprovalPolicy,
  type TerminalApprovalRule,
  type TerminalApprovalSettings,
} from '@terra/shared-dto-chat-types';
import { createTenantStorage } from './tenantStorage';

type TerminalApprovalState = {
  policy: TerminalApprovalPolicy;
  rules: TerminalApprovalRule[];
  setPolicy: (policy: TerminalApprovalPolicy) => void;
  addRule: (rule: TerminalApprovalRule) => boolean;
  removeRule: (rule: TerminalApprovalRule) => void;
  clearRules: () => void;
};

const STORAGE_KEY = 'terminal-approval-settings';

const isSameRule = (a: TerminalApprovalRule, b: TerminalApprovalRule) =>
  a.action === b.action && a.pattern === b.pattern;

export const useTerminalApprovalStore = create<TerminalApprovalState>()(
  persist(
    (set, get) => ({
      policy: DEFAULT_TERMINAL_APPROVAL_POLICY,
      rules: [],
      setPolicy: (policy) => set({ policy }),
      addRule: (candidate) => {
        const parsed = TerminalApprovalRuleSchema.safeParse(candidate);

        if (!parsed.success) {
          return false;
        }

        const rule = parsed.data;
        const { rules } = get();

        if (rules.some((existing) => isSameRule(existing, rule))) {
          return true;
        }

        if (rules.length >= TERMINAL_APPROVAL_RULES_MAX_COUNT) {
          return false;
        }

        set({ rules: [...rules, rule] });

        return true;
      },
      removeRule: (rule) =>
        set((state) => ({
          rules: state.rules.filter((existing) => !isSameRule(existing, rule)),
        })),
      clearRules: () => set({ rules: [] }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: (state) => ({ policy: state.policy, rules: state.rules }),
    },
  ),
);

export const getTerminalApprovalSettings = (): TerminalApprovalSettings => {
  const { policy, rules } = useTerminalApprovalStore.getState();

  return { policy, rules };
};
